import { RefObject, useEffect, useState } from 'react';

/**
 * Whether a horizontally scrolling strip has more content off its left or right edge, and keeps the
 * item marked `data-scroll-active="true"` inside the visible part.
 *
 * A strip that scrolls looks exactly like a truncated one, so without the arrows a user has no
 * reason to think the row continues. Extracted from WizardNavigation, which does the same thing for
 * the step chevrons, once the Settings tab strips needed it too.
 *
 * The ResizeObserver watches the children as well as the strip: several callers size their items
 * after mount (text measurement, icon fonts), so a plain effect runs while the strip still fits and
 * concludes there is nothing to scroll.
 */
export function useScrollHints(
    ref: RefObject<HTMLElement | null>,
    /** Re-runs the centring when this changes -- pass the active tab or step. */
    activeKey?: unknown
): { left: boolean; right: boolean } {
    const [hints, setHints] = useState({ left: false, right: false });

    useEffect(() => {
        const strip = ref.current;
        if (!strip || typeof ResizeObserver === 'undefined') {
            return;
        }

        const showActive = () => {
            const active = strip.querySelector<HTMLElement>('[data-scroll-active="true"]');
            const overflow = strip.scrollWidth - strip.clientWidth;
            if (!active || overflow <= 0) return;
            // Bounding rects rather than offsetLeft: an item's offsetParent is not necessarily this
            // strip, and then offsetLeft is measured from the wrong box.
            const stripBox = strip.getBoundingClientRect();
            const activeBox = active.getBoundingClientRect();
            const target = strip.scrollLeft + (activeBox.left - stripBox.left)
                - (strip.clientWidth - activeBox.width) / 2;
            // Instant: an animated slide on load reads as the page moving by itself.
            strip.scrollTo({ left: Math.max(0, Math.min(target, overflow)), behavior: 'auto' });
        };

        const updateHints = () => {
            const overflow = strip.scrollWidth - strip.clientWidth;
            setHints({
                // 1px of slack: sub-pixel widths leave a fractional remainder at either end.
                left: strip.scrollLeft > 1,
                right: overflow > 1 && strip.scrollLeft < overflow - 1,
            });
        };

        const refresh = () => {
            showActive();
            updateHints();
        };

        refresh();
        const observer = new ResizeObserver(refresh);
        observer.observe(strip);
        for (const child of Array.from(strip.children)) observer.observe(child);
        strip.addEventListener('scroll', updateHints, { passive: true });
        return () => {
            observer.disconnect();
            strip.removeEventListener('scroll', updateHints);
        };
    }, [ref, activeKey]);

    return hints;
}
